import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowBackRounded,
  ArrowForwardRounded,
  DescriptionRounded,
  EmailRounded,
  SchoolRounded,
} from '@mui/icons-material';
import { fetchPublicCatalog, type PublicCatalog } from '../../lib/api/public-catalog.api';
import { buildPublicPath, findProgramBySlug, uniqueCourseCredits } from '../../lib/public-path';
import { resolveDomainTenant } from '../../lib/resolve-tenant-by-domain';
import { LANDING, LANDING_FONT_LINK, landingGold } from './landing-theme';
import { yearsLabel } from './components/ProgramPath';

/**
 * Página pública de un programa: qué es, dónde va en la ruta de formación y qué
 * se estudia, año por año. Todo sale del catálogo público (`getPublicCatalog`);
 * lo que el seminario no cargó simplemente no aparece — nunca texto de relleno.
 *
 * Dos direcciones: `/s/:slug/programas/:code` en el dominio de la plataforma y
 * `/programas/:code` en el dominio propio del seminario.
 */
export default function ProgramPublicPage({ slugOverride }: { slugOverride?: string } = {}) {
  const params = useParams<{ slug: string; code: string }>();
  const slug = slugOverride ?? params.slug ?? '';
  const code = params.code ?? '';
  const basePath = slugOverride ? '' : `/s/${slug}`;
  const { t } = useTranslation('sis');
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState<PublicCatalog | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!slug) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    fetchPublicCatalog(slug)
      .then((c) => !cancelled && setCatalog(c))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const program = catalog ? findProgramBySlug(catalog, code) : undefined;

  useEffect(() => {
    if (program && catalog) document.title = `${program.name} · ${catalog.seminary.name}`;
  }, [program, catalog]);

  const { BG, BG2, BORDER, RULE, RAISED, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_MUTED, SERIF, SANS } =
    LANDING;
  const { GOLD, GOLD_ALPHA } = landingGold(catalog?.seminary.primaryColor);
  const home = basePath || '/';
  // Volver a la escalera (o a la lista) del landing, no al principio de la página.
  const backToPath = `${basePath || '/'}#programas`;

  if (!catalog && !failed) {
    return <div style={{ minHeight: '100vh', background: BG }} />;
  }

  if (!catalog || !program) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: BG,
          color: TEXT_SECONDARY,
          fontFamily: SANS,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <style>{LANDING_FONT_LINK}</style>
        <h1 style={{ fontFamily: SERIF, fontWeight: 500, color: TEXT_PRIMARY, margin: 0 }}>
          {t('publicLanding.program.notFoundTitle')}
        </h1>
        <p style={{ margin: 0 }}>{t('publicLanding.program.notFoundBody')}</p>
        {catalog && (
          <Link to={home} style={{ color: GOLD, marginTop: 8 }}>
            {t('publicLanding.program.backHome')}
          </Link>
        )}
      </div>
    );
  }

  const path = buildPublicPath(catalog);
  const rung = path?.rungs.find((r) => r.program.id === program.id);
  const terms = program.curriculum?.terms ?? [];
  const inYears = terms.length > 0 && terms.every((tm) => tm.year !== undefined);
  const groups = inYears
    ? [...new Set(terms.map((tm) => tm.year!))]
        .sort((a, b) => a - b)
        .map((year) => ({ key: year, terms: terms.filter((tm) => tm.year === year) }))
    : [...terms]
        .sort((a, b) => a.termOffset - b.termOffset)
        .map((tm) => ({ key: tm.termOffset + 1, terms: [tm] }));
  const ownIds = [...new Set(terms.flatMap((tm) => tm.courseIds))].filter(
    (id) => catalog.courses[id],
  );
  const ownCredits = uniqueCourseCredits(ownIds, catalog.courses);
  const years = rung?.years.length ? rung.years : inYears ? groups.map((g) => g.key) : [];
  const included = new Set(rung?.includedProgramIds ?? []);
  const prevColumns = new Set(
    (path?.rungs ?? [])
      .filter((r) => included.has(r.program.id) || r.program.id === rung?.requires?.id)
      .flatMap((r) => Array.from({ length: r.span }, (_, k) => r.startColumn + k)),
  );
  const description = program.purpose ?? program.description;
  const applyHref = `/s/${catalog.seminary.slug}/apply?program=${encodeURIComponent(program.id)}`;

  return (
    <div className="pp" style={{ background: BG, minHeight: '100vh', color: TEXT_PRIMARY }}>
      <style>{`
        ${LANDING_FONT_LINK}
        .pp, .pp * { box-sizing: border-box; }
        .pp { font-family: ${SANS}; -webkit-font-smoothing: antialiased; }
        .pp-wrap { max-width: 1160px; margin: 0 auto; padding: 0 56px; }
        .pp-nav { display: flex; align-items: center; gap: 10px; height: 64px; border-bottom: 1px solid ${BORDER}; }
        .pp-brand { display: flex; align-items: center; gap: 10px; color: ${TEXT_PRIMARY}; text-decoration: none; min-width: 0; }
        .pp-brand span { font-family: ${SERIF}; font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pp-label { font-size: 11px; font-weight: 600; letter-spacing: .2em; text-transform: uppercase; color: ${GOLD}; margin: 0; }
        .pp-hero { padding: 36px 0 56px; border-bottom: 1px solid ${BORDER}; background: radial-gradient(70% 60% at 100% 0%, ${GOLD_ALPHA(0.08)}, transparent 70%); }
        .pp-crumb { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; color: ${TEXT_SECONDARY}; text-decoration: none; }
        .pp-crumb:hover { color: ${TEXT_PRIMARY}; }
        .pp-top { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 56px; align-items: end; margin-top: 36px; }
        .pp-title { font-family: ${SERIF}; font-weight: 500; font-size: clamp(2.4rem, 5vw, 4.2rem); line-height: 1.04; margin: 10px 0 0; text-wrap: balance; }
        .pp-facts { display: flex; flex-wrap: wrap; margin-top: 28px; border-top: 1px solid ${RULE}; border-bottom: 1px solid ${RULE}; }
        .pp-fact { padding: 16px 28px 16px 0; margin-right: 28px; border-right: 1px solid ${RULE}; }
        .pp-fact:last-child { border-right: 0; margin-right: 0; }
        .pp-fact small { display: block; font-size: 10px; font-weight: 600; letter-spacing: .18em; text-transform: uppercase; color: ${TEXT_MUTED}; }
        .pp-fact b { display: block; margin-top: 4px; font-family: ${SERIF}; font-weight: 500; font-size: 26px; font-variant-numeric: lining-nums; }
        .pp-panel { border: 1px solid ${RULE}; border-radius: 3px; padding: 20px; background: ${RAISED}; }
        .pp-panel h2 { margin: 0 0 12px; font-size: 10.5px; font-weight: 600; letter-spacing: .18em; text-transform: uppercase; color: ${TEXT_MUTED}; }
        .pp-panel p { margin: 0; font-size: 13.5px; line-height: 1.55; color: ${TEXT_SECONDARY}; }
        .pp-panel p b { color: ${TEXT_PRIMARY}; font-weight: 500; }
        .pp-mini { display: grid; gap: 4px; margin-bottom: 12px; }
        .pp-mini i { height: 10px; border-radius: 2px; background: rgba(232,228,220,.07); }
        .pp-mini i.on { background: ${GOLD}; }
        .pp-mini i.prev { background: ${GOLD_ALPHA(0.3)}; }
        .pp-body { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 56px; padding: 56px 0 96px; }
        .pp-purpose { font-family: ${SERIF}; font-size: 22px; line-height: 1.5; max-width: 58ch; margin: 14px 0 0; }
        .pp-objs { list-style: none; padding: 0; margin: 16px 0 0; display: grid; gap: 10px; }
        .pp-objs li { display: grid; grid-template-columns: 22px 1fr; gap: 10px; font-size: 14.5px; line-height: 1.55; color: ${TEXT_SECONDARY}; }
        .pp-objs li::before { content: ""; width: 6px; height: 6px; margin: 8px 0 0 6px; border-radius: 50%; background: ${GOLD}; }
        .pp-sec { margin-top: 44px; }
        .pp-sec:first-child { margin-top: 0; }
        .pp-malla-head { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; flex-wrap: wrap; }
        .pp-malla-head h2 { font-family: ${SERIF}; font-weight: 500; font-size: 30px; margin: 8px 0 0; }
        .pp-malla-head p { margin: 0; font-size: 12.5px; color: ${TEXT_MUTED}; }
        .pp-malla-head p.pp-label { font-size: 11px; color: ${GOLD}; }
        .pp-cta.pp-apply-top { display: none; }
        .pp-year { margin-top: 24px; border: 1px solid ${RULE}; border-radius: 3px; overflow: hidden; }
        .pp-year-head { display: flex; justify-content: space-between; align-items: baseline; padding: 14px 20px; background: rgba(255,255,255,.02); border-bottom: 1px solid ${RULE}; }
        .pp-year-head b { font-family: ${SERIF}; font-weight: 500; font-size: 22px; font-variant-numeric: lining-nums; }
        .pp-year-head span { font-size: 12px; color: ${TEXT_SECONDARY}; font-variant-numeric: tabular-nums; }
        .pp-sem { padding: 12px 16px 0; font-size: 10.5px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: ${TEXT_MUTED}; }
        .pp-courses { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; padding: 14px 16px 18px; }
        .pp-course { display: grid; grid-template-columns: 1fr auto; gap: 2px 10px; align-items: baseline; padding: 10px 12px; border-radius: 2px; background: ${BG2}; border: 1px solid rgba(232,228,220,.05); }
        .pp-course .n { font-size: 14px; line-height: 1.35; }
        .pp-course .c { grid-column: 1; font-size: 10.5px; letter-spacing: .12em; color: ${TEXT_MUTED}; }
        .pp-course .cr { grid-row: 1 / 3; grid-column: 2; align-self: center; font-family: ${SERIF}; font-size: 18px; color: ${GOLD}; font-variant-numeric: lining-nums; white-space: nowrap; }
        .pp-elect { padding: 0 16px 16px; font-size: 12.5px; color: ${TEXT_SECONDARY}; }
        .pp-cum { margin-top: 24px; border: 1px dashed ${GOLD_ALPHA(0.4)}; border-radius: 3px; padding: 18px 20px; display: flex; justify-content: space-between; gap: 16px; align-items: center; flex-wrap: wrap; }
        .pp-cum p { margin: 0; color: ${TEXT_SECONDARY}; font-size: 14px; line-height: 1.5; max-width: 60ch; }
        .pp-side { display: flex; flex-direction: column; gap: 16px; }
        .pp-cta { display: inline-flex; justify-content: center; align-items: center; gap: 10px; width: 100%; margin-top: 14px; background: ${GOLD}; color: #0d0f0f; border: 0; padding: 14px 20px; border-radius: 2px; font-size: 12.5px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; cursor: pointer; text-decoration: none; }
        .pp-cta:hover { filter: brightness(1.1); }
        .pp-ghost { display: inline-flex; align-items: center; gap: 8px; border: 1px solid ${BORDER}; padding: 10px 14px; border-radius: 2px; font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: ${TEXT_PRIMARY}; text-decoration: none; }
        .pp-docs { list-style: none; margin: 0; padding: 0; display: grid; gap: 9px; }
        .pp-docs li { display: grid; grid-template-columns: 18px 1fr; gap: 8px; font-size: 13.5px; line-height: 1.45; color: ${TEXT_SECONDARY}; }
        .pp-docs small { color: ${TEXT_MUTED}; }
        .pp a:focus-visible { outline: 2px solid ${GOLD_ALPHA(0.8)}; outline-offset: 3px; }
        .pp-foot { border-top: 1px solid ${BORDER}; padding: 28px 0; font-size: 12px; color: ${TEXT_MUTED}; }
        @media (max-width: 900px) {
          .pp-wrap { padding: 0 24px; }
          .pp-top, .pp-body { grid-template-columns: minmax(0, 1fr); gap: 32px; }
        }
        @media (max-width: 640px) {
          .pp-courses { grid-template-columns: minmax(0, 1fr); }
          .pp-fact { padding-right: 18px; margin-right: 18px; }
          .pp-fact b { font-size: 22px; }
          .pp-cta.pp-apply-top { display: flex; }
          .pp-side .pp-apply { display: none; }
        }
      `}</style>

      <div className="pp-wrap">
        <nav className="pp-nav">
          <Link to={home} className="pp-brand">
            {catalog.seminary.logoUrl ? (
              <img
                src={catalog.seminary.logoUrl}
                alt=""
                style={{ height: 28, objectFit: 'contain' }}
              />
            ) : (
              <SchoolRounded style={{ color: GOLD, fontSize: 22 }} />
            )}
            <span>{catalog.seminary.name}</span>
          </Link>
        </nav>
      </div>

      <header className="pp-hero">
        <div className="pp-wrap">
          <Link to={backToPath} className="pp-crumb">
            <ArrowBackRounded style={{ fontSize: 16 }} /> {t('publicLanding.program.back')}
          </Link>
          <div className="pp-top">
            <div>
              <p className="pp-label">
                {years.length > 0
                  ? t('publicLanding.program.eyebrow', {
                      code: program.code,
                      years: yearsLabel(t, years),
                    })
                  : program.code}
              </p>
              <h1 className="pp-title">{program.name}</h1>
              {(years.length > 0 || ownIds.length > 0 || program.modality) && (
                <div className="pp-facts">
                  {years.length > 0 && (
                    <div className="pp-fact">
                      <small>{t('publicLanding.program.duration')}</small>
                      <b>{t('publicLanding.program.durationYears', { count: years.length })}</b>
                    </div>
                  )}
                  {ownIds.length > 0 && (
                    <>
                      <div className="pp-fact">
                        <small>{t('publicLanding.program.credits')}</small>
                        <b>{ownCredits}</b>
                      </div>
                      <div className="pp-fact">
                        <small>{t('publicLanding.program.courses')}</small>
                        <b>{ownIds.length}</b>
                      </div>
                    </>
                  )}
                  {program.modality && (
                    <div className="pp-fact">
                      <small>{t('publicLanding.program.modalityLabel')}</small>
                      <b>{t(`publicLanding.program.modality.${program.modality}`)}</b>
                    </div>
                  )}
                </div>
              )}
            </div>
            {catalog.seminary.applyEnabled && (
              <a
                className="pp-cta pp-apply-top"
                href={applyHref}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(applyHref);
                }}
              >
                {t('publicLanding.program.applyButton')}
                <ArrowForwardRounded style={{ fontSize: 16 }} />
              </a>
            )}
            {rung && path && (
              <div className="pp-panel">
                <h2>{t('publicLanding.program.placeTitle')}</h2>
                <div
                  className="pp-mini"
                  style={{ gridTemplateColumns: `repeat(${path.columns.length}, 1fr)` }}
                >
                  {path.columns.map((_c, i) => {
                    const on = i >= rung.startColumn && i < rung.startColumn + rung.span;
                    return <i key={i} className={on ? 'on' : prevColumns.has(i) ? 'prev' : ''} />;
                  })}
                </div>
                <p>
                  {rung.requires
                    ? t('publicLanding.program.requires', { name: rung.requires.name })
                    : t('publicLanding.program.entry')}
                </p>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="pp-wrap">
        <div className="pp-body">
          <div>
            {description && (
              <section className="pp-sec">
                <p className="pp-label">{t('publicLanding.program.purpose')}</p>
                <p className="pp-purpose">{description}</p>
              </section>
            )}
            {program.objectives.length > 0 && (
              <section className="pp-sec">
                <p className="pp-label">{t('publicLanding.program.objectives')}</p>
                <ul className="pp-objs">
                  {program.objectives.map((o, i) => (
                    <li key={i}>{o}</li>
                  ))}
                </ul>
              </section>
            )}
            {program.curriculum && ownIds.length > 0 && (
              <section className="pp-sec">
                <div className="pp-malla-head">
                  <div>
                    <p className="pp-label">
                      {t('publicLanding.program.curriculumLabel', {
                        year: program.curriculum.catalogYear,
                      })}
                    </p>
                    <h2>{t('publicLanding.program.curriculumTitle')}</h2>
                  </div>
                  <p>{t('publicLanding.program.curriculumHint')}</p>
                </div>
                {groups.map((g) => {
                  const ids = [...new Set(g.terms.flatMap((tm) => tm.courseIds))].filter(
                    (id) => catalog.courses[id],
                  );
                  const electives = g.terms.reduce((s, tm) => s + (tm.electiveSlots ?? 0), 0);
                  const split = g.terms.length > 1;
                  return (
                    <div key={g.key} className="pp-year">
                      <div className="pp-year-head">
                        <b>
                          {inYears
                            ? t('publicLanding.program.yearHead', { n: g.key })
                            : t('publicLanding.program.termHead', { n: g.key })}
                        </b>
                        <span>
                          {t('publicLanding.path.courses', { count: ids.length })} ·{' '}
                          {t('publicLanding.path.credits', {
                            count: uniqueCourseCredits(ids, catalog.courses),
                          })}
                        </span>
                      </div>
                      {g.terms.map((tm, k) => (
                        <div key={k}>
                          {split && (
                            <div className="pp-sem">
                              {tm.label ??
                                t('publicLanding.program.semesterHead', {
                                  n: tm.semester ?? k + 1,
                                })}
                            </div>
                          )}
                          <div className="pp-courses">
                            {tm.courseIds
                              .filter((id) => catalog.courses[id])
                              .map((id) => {
                                const c = catalog.courses[id]!;
                                return (
                                  <div key={id} className="pp-course">
                                    <span className="n">{c.name}</span>
                                    <span className="c">{c.code}</span>
                                    <span className="cr">
                                      {t('publicLanding.program.creditsShort', { n: c.credits })}
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                      {electives > 0 && (
                        <div className="pp-elect">
                          {t('publicLanding.program.electives', { count: electives })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {rung && rung.cumulativeCredits !== null && (
                  <div className="pp-cum">
                    <p>
                      {t('publicLanding.program.cumulative', {
                        prev: rung.cumulativeCredits - rung.credits,
                        own: rung.credits,
                        total: rung.cumulativeCredits,
                      })}
                    </p>
                    <Link to={backToPath} className="pp-ghost">
                      {t('publicLanding.program.seePath')}
                    </Link>
                  </div>
                )}
              </section>
            )}
          </div>

          <aside className="pp-side">
            {catalog.seminary.applyEnabled && (
              <div className="pp-panel pp-apply">
                <h2>{t('publicLanding.program.applyTitle')}</h2>
                <p>{t('publicLanding.program.applyBody')}</p>
                <a
                  className="pp-cta"
                  href={applyHref}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(applyHref);
                  }}
                >
                  {t('publicLanding.program.applyButton')}
                  <ArrowForwardRounded style={{ fontSize: 16 }} />
                </a>
              </div>
            )}
            {program.documents.length > 0 && (
              <div className="pp-panel">
                <h2>{t('publicLanding.program.documents')}</h2>
                <ul className="pp-docs">
                  {program.documents.map((d, i) => (
                    <li key={i}>
                      <DescriptionRounded style={{ fontSize: 16, color: GOLD, marginTop: 1 }} />
                      <span>
                        {d.label}
                        {!d.required && <small> · {t('publicLanding.program.optional')}</small>}
                        {d.description && (
                          <>
                            <br />
                            <small>{d.description}</small>
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="pp-panel">
              <h2>{t('publicLanding.program.tuitionTitle')}</h2>
              <p>{t('publicLanding.program.tuitionBody')}</p>
              {catalog.seminary.contactEmail && (
                <ul className="pp-docs" style={{ marginTop: 12 }}>
                  <li>
                    <EmailRounded style={{ fontSize: 16, color: GOLD, marginTop: 1 }} />
                    <span style={{ userSelect: 'all' }}>{catalog.seminary.contactEmail}</span>
                  </li>
                </ul>
              )}
            </div>
          </aside>
        </div>
      </main>

      <footer className="pp-wrap">
        <div className="pp-foot">
          {t('publicLanding.footer.rights', {
            year: new Date().getFullYear(),
            name: catalog.seminary.name,
          })}
        </div>
      </footer>
    </div>
  );
}

/**
 * `/programas/:code` en el dominio propio de un seminario: el seminario sale del
 * nombre de dominio, igual que el landing en `/`.
 */
export function CustomDomainProgramPage() {
  const [slug, setSlug] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    resolveDomainTenant()
      .then((r) => !cancelled && setSlug(r.mode === 'resolved' ? r.slug : null))
      .catch(() => !cancelled && setSlug(null));
    return () => {
      cancelled = true;
    };
  }, []);
  if (slug === undefined) return <div style={{ minHeight: '100vh', background: LANDING.BG }} />;
  return <ProgramPublicPage slugOverride={slug ?? ''} />;
}
